import { listMetalGroups, listProductGroups, showProductGroup } from "../erpClient";
import { getSessionBranchId } from "./sessionBranch";
import { extractListRowsFromResponse } from "./parseApiPagination";
import { enrichProductGroupOption, productGroupOptionDisplayName } from "./productGroupPool";

const MIN_PRODUCT_GROUP_SEARCH_LEN = 3;

let metalGroupsMapCache = null;
let metalGroupsLoadPromise = null;

function buildMetalGroupsMap(metalGroupsResponse) {
  const map = {};
  const mgList = extractListRowsFromResponse(metalGroupsResponse, [
    "metal_groups",
    "records",
    "items",
  ]);
  mgList.forEach((group) => {
    const rawMetal =
      group.metal_type ?? group.metal ?? group.Metal ?? "";
    // Line purity must come from metal-group `purity` only (not carat/touch).
    const rawPurity = group.purity;
    const rawCarat = group.carat;
    map[group.id] = {
      purity: rawPurity != null && rawPurity !== "" ? String(rawPurity) : "",
      carat:
        rawCarat != null && rawCarat !== "" ? String(rawCarat) : "",
      metal: rawMetal != null ? String(rawMetal) : "",
    };
  });
  return map;
}

/** Load metal groups once per session (shared across product group list/search). */
async function getMetalGroupsMap() {
  if (metalGroupsMapCache) return metalGroupsMapCache;

  if (!metalGroupsLoadPromise) {
    metalGroupsLoadPromise = listMetalGroups()
      .then((res) => {
        metalGroupsMapCache = buildMetalGroupsMap(res);
        return metalGroupsMapCache;
      })
      .catch(() => {
        metalGroupsMapCache = {};
        return metalGroupsMapCache;
      })
      .finally(() => {
        metalGroupsLoadPromise = null;
      });
  }

  return metalGroupsLoadPromise;
}

export function clearProductGroupMetalGroupsCache() {
  metalGroupsMapCache = null;
  metalGroupsLoadPromise = null;
}

/**
 * Load product group options (with untagged pool balances when API returns them).
 */
export async function fetchProductGroupSelectOptions(filters = {}) {
  const branch_id = filters.branch_id ?? getSessionBranchId();
  const { enrichMetal = true, ...listFilters } = filters;
  const response = await listProductGroups({
    ...listFilters,
    branch_id,
  });
  if (!response?.success || !response.data) return [];

  const metalGroupsMap =
    enrichMetal === false ? {} : await getMetalGroupsMap();

  const pgList = extractListRowsFromResponse(response, ["product_groups"]);

  const productGroups = pgList.map((group) => {
    const enriched = enrichProductGroupOption(group, metalGroupsMap);
    return enriched;
  });

  return Array.from(
    new Map(productGroups.map((item) => [item.value, item])).values(),
  );
}

/** API search — returns limited rows when user types 3+ characters. */
export async function searchProductGroupSelectOptions(
  searchText,
  filters = {},
) {
  const trimmed = String(searchText || "").trim();
  if (trimmed.length < MIN_PRODUCT_GROUP_SEARCH_LEN) return [];
  return fetchProductGroupSelectOptions({
    ...filters,
    search: trimmed,
    enrichMetal: false,
  });
}

/**
 * Attach purity/metal from metal groups when a product group is selected.
 * Does not call product-group list/search — only listMetalGroups (cached).
 */
export async function enrichProductGroupSelectOption(option) {
  if (!option) return null;
  const metalGroupsMap = await getMetalGroupsMap();
  const group = {
    id: option.value ?? option.id,
    name: option.name || option.label || "",
    short_code: option.short_code || "",
    metal_group_id: option.metal_group_id,
    item_category_id: option.item_category_id,
    item_category_name: option.item_category_name,
    pool_pcs: option.pool_pcs,
    pool_nwt: option.pool_nwt,
  };
  return enrichProductGroupOption(group, metalGroupsMap);
}

/** Resolve a single product group by id (e.g. edit form preload). */
export async function fetchProductGroupOptionById(id, filters = {}) {
  const numId = Number(id);
  if (!Number.isFinite(numId) || numId <= 0) return null;
  const response = await showProductGroup({
    id: numId,
    branch_id: filters.branch_id ?? getSessionBranchId(),
  });
  const group = response?.data?.product_group ?? response?.data;
  if (!response?.success || !group?.id) return null;

  const metalGroupsMap = await getMetalGroupsMap();
  return enrichProductGroupOption(group, metalGroupsMap);
}

export function mergeProductGroupOptions(existing = [], incoming = []) {
  const map = new Map();
  const normalize = (item) => {
    const name = productGroupOptionDisplayName(item);
    return { ...item, name, label: name };
  };
  (existing || []).forEach((item) => map.set(String(item.value), normalize(item)));
  (incoming || []).forEach((item) => map.set(String(item.value), normalize(item)));
  return Array.from(map.values());
}

export { productGroupOptionDisplayName };

/** Display label for Old Gold / Old Silver URD product group. */
export function urdProductGroupLabelForMetalType(metalType) {
  const want = String(metalType || "").toUpperCase();
  if (want === "OLD_SILVER") return "Old Silver";
  if (want === "OLD_GOLD") return "Old Gold";
  return "";
}

/** Pick default product group for Old Gold / Old Silver URD from sale redirect. */
export function defaultProductGroupForMetalType(options, metalType) {
  if (!Array.isArray(options) || !metalType) return null;
  const want = String(metalType).toUpperCase();
  const isSilver = want === "OLD_SILVER";
  const isGold = want === "OLD_GOLD";
  if (!isSilver && !isGold) return null;

  const target = isSilver ? "old silver" : "old gold";
  const shortCode = isSilver ? "OSL" : "OGD";

  // 1. Short code (OGD for old gold, OSL for old silver)
  const byCode = options.find((o) => productGroupShortCode(o).includes(shortCode));
  if (byCode) return byCode.value ?? byCode.id ?? null;

  // 2. Exact name match (e.g. "Old Gold" or "Old Silver")
  const exact = options.find((o) => {
    const label = String(o.name || o.label || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, " ");
    return label === target;
  });
  if (exact) return exact.value ?? exact.id ?? null;

  // 3. Contains "old gold" / "old silver" where "old" and "gold"/"silver" are whole words
  //    (e.g. "24 Carat Old Gold"). Uses word-boundary to prevent "gold gold" from
  //    falsely matching because "old gold" is a substring of "gold gold".
  const phraseMatch = options.find((o) => {
    const label = String(o.name || o.label || "").toLowerCase().trim();
    const hasOldWord = /\bold\b/i.test(label);
    if (!hasOldWord) return false;
    if (isSilver) return /\bsilver\b/i.test(label);
    return /\bgold\b/i.test(label) && !/\bsilver\b/i.test(label);
  });
  if (phraseMatch) return phraseMatch.value ?? phraseMatch.id ?? null;

  // 4. Contains separate whole word "old" AND "gold" (or "silver") using word boundary regex \bold\b
  const wordMatch = options.find((o) => {
    const label = String(o.name || o.label || "").toLowerCase();
    const hasOldWord = /\bold\b/i.test(label);
    if (!hasOldWord) return false;
    if (isSilver) return /\bsilver\b/i.test(label);
    return /\bgold\b/i.test(label) && !/\bsilver\b/i.test(label);
  });
  return wordMatch?.value ?? wordMatch?.id ?? null;
}

/** Find the Old Gold (OGD) / Old Silver (OSL) product group, including its id. */
export async function resolveUrdProductGroupOption(metalType) {
  const want = String(metalType || "").toUpperCase();
  const isSilver = want === "OLD_SILVER";
  const code = isSilver ? "OSL" : "OGD";
  const displayName = isSilver ? "Old Silver" : "Old Gold";

  const isMetalMatch = (opt) => {
    if (!opt) return false;
    const text = `${opt.name || ""} ${opt.label || ""} ${opt.metal || ""}`.toUpperCase();
    if (isSilver) {
      return text.includes("SILVER") && !text.includes("GOLD");
    }
    return text.includes("GOLD") && !text.includes("SILVER");
  };

  const searchTerms = [code, displayName, isSilver ? "Silver" : "Gold"];

  for (const term of searchTerms) {
    try {
      const opts = await searchProductGroupSelectOptions(term);
      if (Array.isArray(opts) && opts.length) {
        const matchedId = defaultProductGroupForMetalType(opts, metalType);
        if (matchedId != null) {
          const matched = opts.find((o) => String(o.value ?? o.id) === String(matchedId));
          if (matched && isMetalMatch(matched)) return matched;
        }

        const byCode = opts.find(
          (o) => productGroupShortCode(o).includes(code) && isMetalMatch(o),
        );
        if (byCode) return byCode;

        const byName = opts.find((o) => {
          const label = `${o.name || ""} ${o.label || ""}`.toUpperCase();
          return label.includes(code) && isMetalMatch(o);
        });
        if (byName) return byName;

        const anyMetalMatch = opts.find(isMetalMatch);
        if (anyMetalMatch) return anyMetalMatch;
      }
    } catch (e) {
      console.warn("Failed searching product group for term", term, e);
    }
  }

  return null;
}

function productGroupShortCode(option) {
  const raw = option?.short_code ?? option?.shortCode ?? "";
  const text = Array.isArray(raw) ? raw.join(",") : String(raw);
  return text
    .split(/[,\s]+/)
    .map((part) => part.trim().toUpperCase())
    .filter(Boolean);
}

